import { useState } from "react";
import { api } from "@/lib/api";
import { useDeepDiveSection } from "@/hooks/use-mock-data";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, FileText, Swords, DollarSign, CheckSquare } from "lucide-react";
import ReactMarkdown from "react-markdown";

const SECTIONS = [
  { key: "competitive_teardown", label: "Competitive Teardown", icon: Swords },
  { key: "mvp_scope", label: "MVP Scope", icon: FileText },
  { key: "revenue_model", label: "Revenue Model", icon: DollarSign },
  { key: "validation_steps", label: "Validation Steps", icon: CheckSquare },
] as const;

interface DeepDiveSectionsProps {
  opportunityId: string;
}

export function DeepDiveSections({ opportunityId }: DeepDiveSectionsProps) {
  return (
    <div className="mb-6">
      <h2 className="font-brand text-lg tracking-wide mb-3">ON-DEMAND ANALYSIS</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {SECTIONS.map((section) => (
          <SectionCard
            key={section.key}
            opportunityId={opportunityId}
            sectionKey={section.key}
            label={section.label}
            Icon={section.icon}
          />
        ))}
      </div>
    </div>
  );
}

function SectionCard({
  opportunityId,
  sectionKey,
  label,
  Icon,
}: {
  opportunityId: string;
  sectionKey: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
}) {
  const { data: cached, refetch } = useDeepDiveSection(opportunityId, sectionKey);
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async (regenerate = false) => {
    setGenerating(true);
    try {
      await api.post(`/api/opportunities/${opportunityId}/deep-dive/${sectionKey}`, {
        regenerate,
      });
      refetch();
    } catch {
      // handled by api.ts
    } finally {
      setGenerating(false);
    }
  };

  const content = cached?.data?.content;

  return (
    <div className="glass-card rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-electric-blue" />
          <span className="text-xs font-semibold text-foreground uppercase tracking-wider">{label}</span>
        </div>
        {content && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleGenerate(true)}
            disabled={generating}
            className="h-6 px-2 text-[10px] text-muted-foreground hover:text-electric-blue"
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            Regenerate
          </Button>
        )}
      </div>

      {generating ? (
        <div className="flex items-center gap-2 py-6 justify-center text-mint/60">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-xs font-mono">Analyzing...</span>
        </div>
      ) : content ? (
        <div className="prose prose-invert prose-sm max-w-none text-[13px] text-foreground/90 leading-relaxed">
          <ReactMarkdown>{content}</ReactMarkdown>
        </div>
      ) : (
        <div className="flex flex-col items-center py-4">
          <p className="text-xs text-muted-foreground mb-2">Not yet generated</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleGenerate()}
            className="text-[10px] border-electric-blue/30 text-electric-blue hover:bg-electric-blue/10"
          >
            Generate {label}
          </Button>
        </div>
      )}
    </div>
  );
}
